<template>
  <div class="route-making-container" dir="rtl">
    <!-- Loading Overlay -->
    <vue-element-loading :active="isLoading" :is-full-screen="false" />

    <!-- ==================== PAGE HEADER ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="rm-breadcrumb-wrap mb-1 d-flex align-center">
          <router-link to="/routes" class="rm-breadcrumb-link">
            الرئيسية
          </router-link>
          <v-icon size="14" color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
          <router-link to="/routes" class="rm-breadcrumb-link">
            المسارات
          </router-link>
          <v-icon size="14" color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
          <span class="rm-breadcrumb-current">تفاصيل المسار</span>
        </div>

        <div class="d-flex align-center gap-3 mt-1">
          <span class="rm-badge-section">المسارات</span>
          <span v-if="route_code" class="rm-code-pill rm-code-pill-lg">{{ route_code }}</span>
          <h1 class="rm-main-title mb-0">تفاصيل المسار</h1>
        </div>
        <p class="rm-subtitle mt-1 mb-0">عرض بيانات المسار ومحطاته وتسعيره وإحصائيات التشغيل</p>
      </div>

      <div class="rm-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="rm-btn-secondary"
          to="/routes"
        >
          <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
          رجوع إلى المسارات
        </v-btn>

        <v-btn
          v-if="route_id"
          depressed
          class="rm-btn-primary"
          @click="navigateToEdit"
        >
          <v-icon right size="18" class="ms-1">mdi-pencil-outline</v-icon>
          تعديل المسار
        </v-btn>
      </div>
    </div>

    <!-- ==================== TOP ROUTE SUMMARY CARDS ==================== -->
    <div class="rm-cards-grid mb-6">
      <!-- Card 1: رقم المسار -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">رقم المسار</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value text-primary-navy">{{ route_code || '—' }}</span>
          </div>
          <span class="rm-stat-caption">المعرف التشغيلي الثابت</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="24" color="#082C69">mdi-pound</v-icon>
        </div>
      </div>

      <!-- Card 2: اسم المسار -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">اسم المسار</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value rm-truncate-title" :title="route_name">{{ route_name || '—' }}</span>
          </div>
          <span class="rm-stat-caption">الاسم المعتمد في المنظومة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="24" color="#082C69">mdi-road-variant</v-icon>
        </div>
      </div>

      <!-- Card 3: خط السير (بداية ← نهاية) -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">خط السير</span>
          <div class="rm-stat-val-wrapper">
            <div v-if="startStopName && endStopName" class="rm-points-summary">
              <span class="rm-point-tag rm-start-tag">{{ startStopName }}</span>
              <v-icon size="14" color="#94A3B8" class="mx-1">mdi-arrow-left</v-icon>
              <span class="rm-point-tag rm-end-tag">{{ endStopName }}</span>
            </div>
            <span v-else class="rm-stat-empty">—</span>
          </div>
          <span class="rm-stat-caption">نقطة الانطلاق إلى نقطة الوصول</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="24" color="#10B981">mdi-ray-start-arrow</v-icon>
        </div>
      </div>

      <!-- Card 4: عدد المحطات -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">عدد المحطات</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value">{{ stops.length }} <small class="text-caption">محطة</small></span>
          </div>
          <span class="rm-stat-caption">إجمالي المحطات المجدولة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="24" color="#082C69">mdi-bus-stop</v-icon>
        </div>
      </div>

      <!-- Card 5: المسافة الكلية -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المسافة الكلية</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="canonicalDistance" class="rm-stat-value">{{ canonicalDistance }} <small class="text-caption">كم</small></span>
            <span v-else class="rm-stat-empty">—</span>
          </div>
          <span class="rm-stat-caption">المسافة الفعلية لمسار Google</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="24" color="#F59E0B">mdi-map-marker-distance</v-icon>
        </div>
      </div>

      <!-- Card 6: الوقت التقريبي -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">الوقت التقريبي</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="approximateDuration" class="rm-stat-value">{{ approximateDuration }}</span>
            <span v-else class="rm-stat-empty">—</span>
          </div>
          <span class="rm-stat-caption">زمن القيادة المقدر للرحلة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="24" color="#F59E0B">mdi-clock-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== ROW 1: [ خريطة المسار (60%) ] [ محطات المسار (40%) ] ==================== -->
    <div class="rm-paired-row mb-6">
      <!-- 1. خريطة المسار -->
      <div class="rm-col-map">
        <div class="rm-card rm-map-card rm-full-height">
          <div class="rm-card-header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="22" color="#082C69">mdi-map-search-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">خريطة المسار</h2>
                <p class="rm-card-subtitle">المسار المعتمد ونقاط المحطات على الخريطة</p>
              </div>
            </div>

            <v-btn
              depressed
              small
              class="rm-btn-secondary"
              @click="fitMapBounds"
            >
              <v-icon size="16" class="me-1">mdi-fit-to-screen-outline</v-icon>
              إظهار المسار بالكامل
            </v-btn>
          </div>

          <!-- Map Component -->
          <div class="rm-map-view-wrapper">
            <GoogleMapLoader
              ref="mapLoader"
              :enabled="false"
              :center="center"
              :selected="selectedItem"
              :zoom="zoom"
              :apiKey="apiKey"
              :markers="markers"
              :polylines="polyline"
            />
          </div>

          <div class="rm-map-footer-legend d-flex align-center justify-between mt-3 pt-2 border-top">
            <div class="d-flex align-center gap-4">
              <span class="rm-legend-item">
                <span class="rm-legend-dot rm-dot-start"></span>
                نقطة البداية ({{ startStopName || 'المحطة الأولى' }})
              </span>
              <span class="rm-legend-item">
                <span class="rm-legend-dot rm-dot-end"></span>
                نقطة النهاية ({{ endStopName || 'المحطة الأخيرة' }})
              </span>
              <span class="rm-legend-item">
                <span class="rm-legend-dot rm-dot-intermediate"></span>
                محطات التوقف الوسيطة
              </span>
            </div>
            <span class="text-caption text--secondary font-weight-medium">
              الطريق المختار: <strong class="text-primary-navy">المسار الفعلي المعتمد (Current = 1)</strong>
            </span>
          </div>
        </div>
      </div>

      <!-- 2. محطات المسار الجدولية -->
      <div class="rm-col-stops">
        <div class="rm-card rm-stops-card">
          <div class="rm-card-header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="22" color="#082C69">mdi-format-list-numbered-rtl</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">محطات المسار</h2>
                <p class="rm-card-subtitle">الترتيب التشغيلي ومسافات وأزمنة الشرائح</p>
              </div>
            </div>
            <span class="rm-table-count">{{ stops.length }} محطة مسجلة</span>
          </div>

          <!-- Stops Table Wrapper (auto-hugs rows, no dead white space) -->
          <div class="rm-stops-scroll-wrapper">
            <table class="rm-modern-table rm-stops-table">
              <thead>
                <tr>
                  <th class="rm-th-order text-center">#</th>
                  <th class="rm-th-station">المحطة</th>
                  <th class="rm-th-address">العنوان</th>
                  <th class="rm-th-dist text-center">المسافة</th>
                  <th class="rm-th-time text-center">الوقت</th>
                  <th class="rm-th-road">المسار المختار</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(stop, index) in stops"
                  :key="stop.place_id || stop.id || index"
                  :class="['rm-table-row', { 'row-active': selectedIdx === index }]"
                  @click="selectStop(stop, index)"
                >
                  <!-- 1. Order Index -->
                  <td class="rm-td-order text-center">
                    <span
                      class="rm-order-badge"
                      :class="{
                        'badge-first': index === 0,
                        'badge-last': index === stops.length - 1 && stops.length > 1,
                        'badge-mid': index > 0 && index < stops.length - 1
                      }"
                    >
                      {{ index + 1 }}
                    </span>
                  </td>

                  <!-- 2. Station Name + Badges -->
                  <td class="rm-td-station">
                    <div class="rm-station-info">
                      <span class="rm-stop-name-bold">{{ stop.name }}</span>
                      <div v-if="index === 0 || (index === stops.length - 1 && stops.length > 1)" class="rm-station-subtags mt-1">
                        <span v-if="index === 0" class="rm-tag-first">
                          <v-icon size="11" color="#10B981" class="me-1">mdi-flag-outline</v-icon>
                          نقطة البداية
                        </span>
                        <span v-else-if="index === stops.length - 1 && stops.length > 1" class="rm-tag-last">
                          <v-icon size="11" color="#EF4444" class="me-1">mdi-flag-checkered</v-icon>
                          نقطة النهاية
                        </span>
                      </div>
                    </div>
                  </td>

                  <!-- 3. Address -->
                  <td class="rm-td-address">
                    <span class="rm-address-text" :title="stop.address || '—'">
                      {{ stop.address || '—' }}
                    </span>
                  </td>

                  <!-- 4. Distance from previous -->
                  <td class="rm-td-dist text-center">
                    <span v-if="index === 0" class="rm-pill-start">
                      الانطلاق
                    </span>
                    <span v-else-if="getLegDistance(index) !== null" class="rm-pill-dist">
                      {{ getLegDistance(index) }} كم
                    </span>
                    <span v-else class="rm-cell-empty">—</span>
                  </td>

                  <!-- 5. Time from previous -->
                  <td class="rm-td-time text-center">
                    <span v-if="index === 0" class="rm-cell-empty">—</span>
                    <span v-else-if="getLegDuration(index)" class="rm-pill-time">
                      {{ getLegDuration(index) }}
                    </span>
                    <span v-else class="rm-cell-empty">—</span>
                  </td>

                  <!-- 6. Selected Road Summary -->
                  <td class="rm-td-road">
                    <div v-if="index > 0 && getLegSummary(index)" class="rm-road-pill" :title="getLegSummary(index)">
                      <v-icon size="13" color="#082C69" class="me-1">mdi-routes</v-icon>
                      <span class="rm-road-text">{{ getLegSummary(index) }}</span>
                    </div>
                    <span v-else class="rm-cell-empty">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== ROW 2: [ تسعير المسار ] [ تقديرات الاشتراكات ] ==================== -->
    <div class="rm-paired-row mb-8">
      <!-- 1. تسعير المسار -->
      <div class="rm-col-pricing">
        <div class="rm-card rm-pricing-card rm-full-height">
          <div class="rm-card-header mb-4">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="22" color="#082C69">mdi-cash-register</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">تسعير المسار</h2>
                <p class="rm-card-subtitle">معايير التسعير المعتمدة للمسار الكلي والشرائح</p>
              </div>
            </div>
          </div>

          <div class="rm-pricing-grid mb-4">
            <!-- Metric 1: سعر الرحلة بالكامل -->
            <div class="rm-price-box">
              <span class="rm-price-label">سعر الرحلة بالكامل</span>
              <div class="rm-price-val-wrap">
                <span v-if="full_trip_price" class="rm-price-val font-weight-bold">{{ full_trip_price }} <small>ج.م</small></span>
                <span v-else class="rm-price-empty">غير محدد</span>
              </div>
              <span class="rm-price-caption">السعر الإجمالي للرحلة كاملة</span>
            </div>

            <!-- Metric 2: نسبة الحد الأدنى -->
            <div class="rm-price-box">
              <span class="rm-price-label">نسبة الحد الأدنى</span>
              <div class="rm-price-val-wrap">
                <span v-if="minimum_percentage" class="rm-price-val">{{ minimum_percentage }} <small>%</small></span>
                <span v-else class="rm-price-empty">غير محدد</span>
              </div>
              <span class="rm-price-caption">النسبة المعتمدة لحساب فتح الرحلة</span>
            </div>

            <!-- Metric 3: قيمة الحد الأدنى -->
            <div class="rm-price-box rm-box-highlight">
              <span class="rm-price-label">قيمة الحد الأدنى (المحسوبة)</span>
              <div class="rm-price-val-wrap">
                <span v-if="calculatedMinimumPrice" class="rm-price-val text-primary-navy">{{ calculatedMinimumPrice }} <small>ج.م</small></span>
                <span v-else class="rm-price-empty">—</span>
              </div>
              <span class="rm-price-caption">سعر الرحلة × نسبة الحد الأدنى</span>
            </div>

            <!-- Metric 4: سعر الكيلو -->
            <div class="rm-price-box rm-box-highlight">
              <span class="rm-price-label">سعر الكيلو (المحسوب)</span>
              <div class="rm-price-val-wrap">
                <span v-if="calculatedPricePerKm" class="rm-price-val text-amber-dark">{{ calculatedPricePerKm }} <small>ج.م/كم</small></span>
                <span v-else class="rm-price-empty">—</span>
              </div>
              <span class="rm-price-caption">(السعر الكلي - الأدنى) ÷ المسافة</span>
            </div>
          </div>

          <div class="rm-info-banner">
            <v-icon size="18" color="#082C69" class="me-2 mt-1">mdi-information-outline</v-icon>
            <div class="rm-info-text">
              <strong>قاعدة احتساب التسعير:</strong>
              جميع القيم المعروضة أعلاه قراءة فقط. يتم تسعير شرائح الركاب وفق المسافة المقطوعة بين محطتي الركوب والنزول باستخدام مسافة Google الرسمية للمسار.
            </div>
          </div>
        </div>
      </div>

      <!-- 2. تقديرات الاشتراكات للمسار بالكامل -->
      <div class="rm-col-subscriptions">
        <div class="rm-card rm-subscriptions-card rm-full-height">
          <div class="rm-card-header mb-4">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="22" color="#082C69">mdi-ticket-percent-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">تقديرات الاشتراكات للمسار بالكامل</h2>
                <p class="rm-card-subtitle">الباقات الأسبوعية والشهرية وفق نسب خصم المسار</p>
              </div>
            </div>
          </div>

          <div class="rm-sub-packages-grid mb-4">
            <!-- Weekly Package -->
            <div class="rm-sub-pkg-card">
              <div class="rm-pkg-top d-flex align-center justify-space-between mb-3">
                <span class="rm-pkg-badge rm-badge-weekly">الباقة الأسبوعية</span>
                <span class="rm-pkg-discount">خصم {{ weekly_discount_percentage || 0 }}%</span>
              </div>

              <div class="rm-pkg-details mb-3">
                <div class="d-flex justify-space-between py-1">
                  <span class="text--secondary text-caption">مدة الباقة:</span>
                  <span class="font-weight-bold text-caption">7 أيام</span>
                </div>
                <div class="d-flex justify-space-between py-1">
                  <span class="text--secondary text-caption">عدد الرحلات:</span>
                  <span class="font-weight-bold text-caption">10 رحلات</span>
                </div>
                <div class="d-flex justify-space-between py-1">
                  <span class="text--secondary text-caption">السعر قبل الخصم:</span>
                  <span class="text-caption text-decoration-line-through text--secondary">{{ weeklyBasePrice ? `${weeklyBasePrice} ج.م` : '—' }}</span>
                </div>
              </div>

              <div class="rm-pkg-total pt-2 border-top d-flex align-center justify-space-between">
                <span class="rm-pkg-total-label">السعر التقديري للمسار:</span>
                <span class="rm-pkg-total-val text-primary-navy">{{ weeklyFinalPrice ? `${weeklyFinalPrice} ج.م` : '—' }}</span>
              </div>
            </div>

            <!-- Monthly Package -->
            <div class="rm-sub-pkg-card">
              <div class="rm-pkg-top d-flex align-center justify-space-between mb-3">
                <span class="rm-pkg-badge rm-badge-monthly">الباقة الشهرية</span>
                <span class="rm-pkg-discount">خصم {{ monthly_discount_percentage || 0 }}%</span>
              </div>

              <div class="rm-pkg-details mb-3">
                <div class="d-flex justify-space-between py-1">
                  <span class="text--secondary text-caption">مدة الباقة:</span>
                  <span class="font-weight-bold text-caption">30 يوم</span>
                </div>
                <div class="d-flex justify-space-between py-1">
                  <span class="text--secondary text-caption">عدد الرحلات:</span>
                  <span class="font-weight-bold text-caption">44 رحلة</span>
                </div>
                <div class="d-flex justify-space-between py-1">
                  <span class="text--secondary text-caption">السعر قبل الخصم:</span>
                  <span class="text-caption text-decoration-line-through text--secondary">{{ monthlyBasePrice ? `${monthlyBasePrice} ج.م` : '—' }}</span>
                </div>
              </div>

              <div class="rm-pkg-total pt-2 border-top d-flex align-center justify-space-between">
                <span class="rm-pkg-total-label">السعر التقديري للمسار:</span>
                <span class="rm-pkg-total-val text-primary-navy">{{ monthlyFinalPrice ? `${monthlyFinalPrice} ج.م` : '—' }}</span>
              </div>
            </div>
          </div>

          <span class="rm-field-hint">
            <strong>ملاحظة:</strong> الأسعار المعروضة أعلاه هي تقديرات للمسار كاملاً. يتم احتساب اشتراك العميل الفعلي وفق محطتي ركوبه ونزوله تلقائياً.
          </span>
        </div>
      </div>
    </div>

    <!-- ==================== PERFORMANCE ANALYTICS SECTION ==================== -->
    <div class="rm-analytics-section mt-4 mb-8">
      <!-- Section Header with Period Switcher -->
      <div class="rm-section-header-row d-flex align-center justify-space-between mb-5">
        <div>
          <div class="d-flex align-center gap-2 mb-1">
            <span class="rm-badge-section rm-badge-analytics">البيانات التشغيلية الفعلية</span>
            <h2 class="rm-section-title mb-0">إحصائيات الأداء</h2>
          </div>
          <p class="rm-section-desc mb-0">مؤشرات تشغيلية حقيقية مستخرجة من قاعدة البيانات للمسار الحالي</p>
        </div>

        <!-- Period Selector -->
        <div class="rm-period-selector d-flex align-center">
          <button
            type="button"
            class="rm-period-btn"
            :class="{ 'period-active': selectedPeriod === 7 }"
            @click="changePeriod(7)"
          >
            آخر 7 أيام
          </button>
          <button
            type="button"
            class="rm-period-btn"
            :class="{ 'period-active': selectedPeriod === 30 }"
            @click="changePeriod(30)"
          >
            آخر 30 يوم
          </button>
          <button
            type="button"
            class="rm-period-btn"
            :class="{ 'period-active': selectedPeriod === 90 }"
            @click="changePeriod(90)"
          >
            آخر 90 يوم
          </button>
        </div>
      </div>

      <!-- Real KPI Cards Grid -->
      <div class="rm-kpi-grid mb-6">
        <!-- KPI 1: إجمالي الحجوزات (المقاعد) -->
        <div class="rm-kpi-card">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="rm-kpi-label">إجمالي المقاعد المحجوزة</span>
            <div class="rm-kpi-icon-box rm-icon-blue">
              <v-icon size="20" color="#082C69">mdi-ticket-confirmation-outline</v-icon>
            </div>
          </div>
          <div class="rm-kpi-val-wrap">
            <span class="rm-kpi-val">{{ analytics.total_bookings }}</span>
            <span class="rm-kpi-unit">مقعد</span>
          </div>
          <span class="rm-kpi-caption">حجوزات مؤكدة (غير ملغاة)</span>
        </div>

        <!-- KPI 2: العملاء الفعليين -->
        <div class="rm-kpi-card">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="rm-kpi-label">الركاب المميزين</span>
            <div class="rm-kpi-icon-box rm-icon-green">
              <v-icon size="20" color="#10B981">mdi-account-group-outline</v-icon>
            </div>
          </div>
          <div class="rm-kpi-val-wrap">
            <span class="rm-kpi-val">{{ analytics.unique_customers }}</span>
            <span class="rm-kpi-unit">عميل</span>
          </div>
          <span class="rm-kpi-caption">مستخدمين فريدين حجزوا بالمسار</span>
        </div>

        <!-- KPI 3: الرحلات المنفذة -->
        <div class="rm-kpi-card">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="rm-kpi-label">الرحلات المنفذة</span>
            <div class="rm-kpi-icon-box rm-icon-blue">
              <v-icon size="20" color="#082C69">mdi-bus-clock</v-icon>
            </div>
          </div>
          <div class="rm-kpi-val-wrap">
            <span class="rm-kpi-val">{{ analytics.completed_trips }}</span>
            <span class="rm-kpi-unit">رحلة</span>
          </div>
          <span class="rm-kpi-caption">من أصل {{ analytics.scheduled_trips }} رحلة مجدولة</span>
        </div>

        <!-- KPI 4: متوسط الركاب -->
        <div class="rm-kpi-card">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="rm-kpi-label">متوسط الركاب</span>
            <div class="rm-kpi-icon-box rm-icon-amber">
              <v-icon size="20" color="#F59E0B">mdi-chart-line</v-icon>
            </div>
          </div>
          <div class="rm-kpi-val-wrap">
            <span class="rm-kpi-val">{{ analytics.average_passengers }}</span>
            <span class="rm-kpi-unit">راكب / رحلة</span>
          </div>
          <span class="rm-kpi-caption">معدل الركاب لكل رحلة مكتملة</span>
        </div>

        <!-- KPI 5: إجمالي الإيرادات المحصلة -->
        <div class="rm-kpi-card">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="rm-kpi-label">الإيرادات المحصلة</span>
            <div class="rm-kpi-icon-box rm-icon-green">
              <v-icon size="20" color="#10B981">mdi-cash-multiple</v-icon>
            </div>
          </div>
          <div class="rm-kpi-val-wrap">
            <span class="rm-kpi-val text-primary-navy">{{ formatCurrency(analytics.revenue) }}</span>
            <span class="rm-kpi-unit">ج.م</span>
          </div>
          <span class="rm-kpi-caption">المدفوعات الفعلية للحجوزات</span>
        </div>

        <!-- KPI 6: معدل الإشغال (Only rendered if reliable bus capacity exists) -->
        <div v-if="analytics.occupancy_rate !== null && analytics.occupancy_rate !== undefined" class="rm-kpi-card">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="rm-kpi-label">معدل الإشغال</span>
            <div class="rm-kpi-icon-box rm-icon-amber">
              <v-icon size="20" color="#F59E0B">mdi-percent</v-icon>
            </div>
          </div>
          <div class="rm-kpi-val-wrap">
            <span class="rm-kpi-val">{{ analytics.occupancy_rate }}</span>
            <span class="rm-kpi-unit">%</span>
          </div>
          <span class="rm-kpi-caption">نسبة المقاعد المشغولة لسعة الحافلات</span>
        </div>
      </div>

      <!-- ROW 3: [ رسم حركة الحجوزات (60%) ] [ المحطات الأكثر استخداماً / أوقات الذروة (40%) ] -->
      <div class="rm-paired-row">
        <!-- 1. رسم حركة الحجوزات اليومية -->
        <div class="rm-col-chart">
          <div class="rm-card rm-chart-card rm-full-height">
            <div class="rm-card-header d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <div class="rm-icon-header-box me-3">
                  <v-icon size="22" color="#082C69">mdi-chart-areaspline</v-icon>
                </div>
                <div>
                  <h3 class="rm-card-title">حركة الحجوزات اليومية</h3>
                  <p class="rm-card-subtitle">توزيع المقاعد المحجوزة والإيرادات على مدار {{ analytics.period_label }}</p>
                </div>
              </div>
            </div>

            <!-- Chart Container -->
            <div v-if="hasChartData" class="rm-apex-wrapper">
              <vue-apex-charts
                type="area"
                height="300"
                :options="chartOptions"
                :series="chartSeries"
              />
            </div>
            <!-- Empty Chart State -->
            <div v-else class="rm-chart-empty py-12 text-center">
              <v-icon size="48" color="#CBD5E1" class="mb-2">mdi-chart-line-variant</v-icon>
              <h4 class="rm-empty-sub mb-1">لا توجد حجوزات مسجلة</h4>
              <p class="rm-empty-text text--secondary mb-0">
                لم يتم تسجيل حجوزات مؤكدة لهذا المسار خلال {{ analytics.period_label }}.
              </p>
            </div>
          </div>
        </div>

        <!-- 2. المحطات الأكثر استخداماً / أوقات الذروة -->
        <div class="rm-col-analytics-tabs">
          <div class="rm-card rm-tabs-card rm-full-height d-flex flex-column">
            <!-- Tabs Header -->
            <div class="rm-analytics-tab-header d-flex align-center justify-space-between mb-4">
              <div class="rm-tabs-pill-wrap">
                <button
                  type="button"
                  class="rm-tab-pill-btn"
                  :class="{ 'active-tab': activeAnalyticsTab === 'stops' }"
                  @click="activeAnalyticsTab = 'stops'"
                >
                  <v-icon size="16" class="me-1">mdi-bus-stop</v-icon>
                  أكثر المحطات استخداماً
                </button>
                <button
                  type="button"
                  class="rm-tab-pill-btn"
                  :class="{ 'active-tab': activeAnalyticsTab === 'peaks' }"
                  @click="activeAnalyticsTab = 'peaks'"
                >
                  <v-icon size="16" class="me-1">mdi-clock-fast</v-icon>
                  أوقات الذروة
                </button>
              </div>
            </div>

            <!-- Tab 1: أكثر المحطات استخداماً -->
            <div v-show="activeAnalyticsTab === 'stops'" class="rm-tab-content-wrap flex-grow-1">
              <div v-if="analytics.most_used_stops && analytics.most_used_stops.length > 0 && hasStopsActivity" class="rm-stops-activity-list">
                <div
                  v-for="(st, idx) in activeStopsList"
                  :key="st.stop_id || idx"
                  class="rm-station-activity-row"
                >
                  <div class="d-flex align-center justify-space-between mb-1">
                    <div class="d-flex align-center">
                      <span class="rm-rank-circle me-2">{{ idx + 1 }}</span>
                      <span class="rm-st-name font-weight-bold text-dark">{{ st.stop_name }}</span>
                    </div>
                    <span class="rm-st-total font-weight-bold text-primary-navy">
                      {{ st.total_count }} <small class="text--secondary">حركة</small>
                    </span>
                  </div>

                  <!-- Progress Bar -->
                  <div class="rm-progress-track mb-2">
                    <div
                      class="rm-progress-bar"
                      :style="{ width: getStopProgressWidth(st.total_count) + '%' }"
                    ></div>
                  </div>

                  <div class="d-flex align-center justify-between text-caption text--secondary">
                    <span>
                      <v-icon size="12" color="#10B981" class="me-1">mdi-arrow-up-circle</v-icon>
                      صعود: <strong>{{ st.pickup_count }}</strong>
                    </span>
                    <span>
                      <v-icon size="12" color="#EF4444" class="me-1">mdi-arrow-down-circle</v-icon>
                      نزول: <strong>{{ st.dropoff_count }}</strong>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Empty State for Stops Activity -->
              <div v-else class="rm-activity-empty py-10 text-center">
                <v-icon size="42" color="#CBD5E1" class="mb-2">mdi-bus-stop</v-icon>
                <p class="rm-empty-text text--secondary mb-0">
                  لا توجد بيانات صعود أو نزول مسجلة للمحطات خلال هذه الفترة.
                </p>
              </div>
            </div>

            <!-- Tab 2: أوقات الذروة -->
            <div v-show="activeAnalyticsTab === 'peaks'" class="rm-tab-content-wrap flex-grow-1">
              <div v-if="analytics.peak_times && analytics.peak_times.length > 0" class="rm-peaks-list">
                <div
                  v-for="(pk, idx) in analytics.peak_times.slice(0, 6)"
                  :key="pk.hour || idx"
                  class="rm-peak-row"
                >
                  <div class="d-flex align-center justify-space-between mb-1">
                    <div class="d-flex align-center">
                      <v-icon size="16" color="#F59E0B" class="me-2">mdi-clock-time-four-outline</v-icon>
                      <span class="rm-peak-time font-weight-bold">{{ pk.time_range }}</span>
                    </div>
                    <span class="rm-peak-count font-weight-bold text-primary-navy">
                      {{ pk.count }} <small class="text--secondary">حجز</small>
                    </span>
                  </div>

                  <!-- Peak Progress -->
                  <div class="rm-progress-track">
                    <div
                      class="rm-progress-bar rm-bar-amber"
                      :style="{ width: getPeakProgressWidth(pk.count) + '%' }"
                    ></div>
                  </div>
                </div>
              </div>

              <!-- Empty State for Peaks -->
              <div v-else class="rm-activity-empty py-10 text-center">
                <v-icon size="42" color="#CBD5E1" class="mb-2">mdi-clock-outline</v-icon>
                <p class="rm-empty-text text--secondary mb-0">
                  لا توجد بيانات أوقات ذروة مسجلة خلال هذه الفترة.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import VueElementLoading from "vue-element-loading";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";
import VueApexCharts from "vue-apexcharts";
import { Keys } from "/src/config.js";

export default {
  name: "RouteMakingViewRoute",
  components: {
    VueElementLoading,
    GoogleMapLoader,
    VueApexCharts,
  },
  data() {
    return {
      apiKey: Keys.GOOGLE_MAPS_API_KEY,
      route_id: null,
      route_code: "",
      route_name: "",
      full_trip_price: null,
      minimum_percentage: null,
      weekly_discount_percentage: null,
      monthly_discount_percentage: null,
      canonicalDistance: null,
      approximate_duration_server: null,
      total_duration_seconds: null,
      stops: [],
      directions: [],
      markers: [],
      polyline: [],
      selectedItem: null,
      selectedIdx: null,
      center: {
        lat: Keys.VUE_APP_ORIGIN_LAT ? parseFloat(Keys.VUE_APP_ORIGIN_LAT) : 30.0444,
        lng: Keys.VUE_APP_ORIGIN_LNG ? parseFloat(Keys.VUE_APP_ORIGIN_LNG) : 31.2357,
      },
      zoom: 12,
      isLoading: false,
      selectedPeriod: 30,
      activeAnalyticsTab: "stops",
      analytics: {
        period: 30,
        period_label: "آخر 30 يوم",
        total_bookings: 0,
        unique_customers: 0,
        scheduled_trips: 0,
        completed_trips: 0,
        cancelled_trips: 0,
        average_passengers: 0,
        occupancy_rate: null,
        revenue: 0.0,
        most_used_stops: [],
        peak_times: [],
        daily_trend: [],
      },
      colors: [
        "#082C69",
        "#10B981",
        "#F59E0B",
        "#3B82F6",
        "#8B5CF6",
        "#EC4899",
        "#06B6D4",
        "#EF4444",
      ],
    };
  },
  computed: {
    startStopName() {
      if (this.stops && this.stops.length > 0) {
        return this.stops[0].name || "";
      }
      return "";
    },
    endStopName() {
      if (this.stops && this.stops.length > 1) {
        return this.stops[this.stops.length - 1].name || "";
      }
      return "";
    },
    calculatedMinimumPrice() {
      const f = parseFloat(this.full_trip_price);
      const p = parseFloat(this.minimum_percentage);
      if (!isNaN(f) && f > 0 && !isNaN(p) && p > 0) {
        return ((f * p) / 100).toFixed(2);
      }
      return null;
    },
    calculatedPricePerKm() {
      const f = parseFloat(this.full_trip_price);
      const minPrice = parseFloat(this.calculatedMinimumPrice);
      const d = parseFloat(this.canonicalDistance);
      if (!isNaN(f) && f > 0 && !isNaN(minPrice) && !isNaN(d) && d > 0) {
        const rem = f - minPrice;
        return (rem / d).toFixed(2);
      }
      return null;
    },
    weeklyBasePrice() {
      const f = parseFloat(this.full_trip_price);
      if (!isNaN(f) && f > 0) {
        return (f * 7).toFixed(2);
      }
      return null;
    },
    weeklyFinalPrice() {
      const base = parseFloat(this.weeklyBasePrice);
      const disc = parseFloat(this.weekly_discount_percentage);
      if (!isNaN(base) && base > 0) {
        const discountAmount = (!isNaN(disc) && disc >= 0) ? (base * disc) / 100 : 0;
        return Math.max(0, base - discountAmount).toFixed(2);
      }
      return null;
    },
    monthlyBasePrice() {
      const f = parseFloat(this.full_trip_price);
      if (!isNaN(f) && f > 0) {
        return (f * 30).toFixed(2);
      }
      return null;
    },
    monthlyFinalPrice() {
      const base = parseFloat(this.monthlyBasePrice);
      const disc = parseFloat(this.monthly_discount_percentage);
      if (!isNaN(base) && base > 0) {
        const discountAmount = (!isNaN(disc) && disc >= 0) ? (base * disc) / 100 : 0;
        return Math.max(0, base - discountAmount).toFixed(2);
      }
      return null;
    },
    approximateDuration() {
      if (this.approximate_duration_server) {
        return this.approximate_duration_server;
      }
      if (!this.directions || this.directions.length === 0) return null;
      let totalSeconds = 0;
      let hasDuration = false;
      for (let i = 0; i < this.directions.length; i++) {
        const legDirs = this.directions[i];
        if (Array.isArray(legDirs)) {
          const currentDir = legDirs.find((d) => d.current == 1 || d.current === true) || legDirs[0];
          if (currentDir && currentDir.duration) {
            const sec = parseInt(currentDir.duration, 10);
            if (!isNaN(sec) && sec > 0) {
              totalSeconds += sec;
              hasDuration = true;
            }
          }
        }
      }
      if (!hasDuration || totalSeconds <= 0) return null;
      return this.formatDurationArabic(totalSeconds);
    },
    hasChartData() {
      return (
        this.analytics.daily_trend &&
        this.analytics.daily_trend.length > 0 &&
        this.analytics.daily_trend.some((d) => d.bookings > 0)
      );
    },
    chartSeries() {
      if (!this.hasChartData) return [];
      const bookingsData = this.analytics.daily_trend.map((d) => d.bookings);
      return [
        {
          name: "عدد المقاعد المحجوزة",
          data: bookingsData,
        },
      ];
    },
    chartOptions() {
      const categories = (this.analytics.daily_trend || []).map((d) => {
        // Format YYYY-MM-DD to DD/MM
        if (d.date && d.date.includes("-")) {
          const parts = d.date.split("-");
          return `${parts[2]}/${parts[1]}`;
        }
        return d.date || "";
      });

      return {
        chart: {
          type: "area",
          height: 300,
          toolbar: { show: false },
          fontFamily: "Cairo, sans-serif",
          zoom: { enabled: false },
        },
        colors: ["#082C69"],
        dataLabels: { enabled: false },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [20, 100, 100, 100],
          },
        },
        xaxis: {
          categories,
          labels: {
            style: {
              colors: "#64748B",
              fontFamily: "Cairo, sans-serif",
              fontSize: "11px",
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: {
              colors: "#64748B",
              fontFamily: "Cairo, sans-serif",
              fontSize: "11px",
            },
          },
        },
        grid: {
          borderColor: "#F1F5F9",
          strokeDashArray: 4,
        },
        tooltip: {
          theme: "light",
          style: {
            fontFamily: "Cairo, sans-serif",
          },
          y: {
            formatter: (val) => `${val} مقعد`,
          },
        },
      };
    },
    hasStopsActivity() {
      return (
        this.analytics.most_used_stops &&
        this.analytics.most_used_stops.some((s) => s.total_count > 0)
      );
    },
    activeStopsList() {
      if (!this.analytics.most_used_stops) return [];
      return this.analytics.most_used_stops.filter((s) => s.total_count > 0);
    },
    maxStopActivity() {
      if (!this.activeStopsList || this.activeStopsList.length === 0) return 1;
      return Math.max(...this.activeStopsList.map((s) => s.total_count), 1);
    },
    maxPeakActivity() {
      if (!this.analytics.peak_times || this.analytics.peak_times.length === 0) return 1;
      return Math.max(...this.analytics.peak_times.map((p) => p.count), 1);
    },
  },
  mounted() {
    if (this.$route.params.route_id) {
      this.route_id = this.$route.params.route_id;
      this.loadRouteDetails(this.selectedPeriod);
    } else {
      this.$notify({
        title: "تنبيه",
        text: "لم يتم تحديد معرّف المسار",
        type: "warning",
      });
      this.$router.push("/routes");
    }
  },
  methods: {
    loadRouteDetails(period = 30) {
      this.isLoading = true;
      axios
        .get(`/routes/${this.route_id}?period=${period}`)
        .then((response) => {
          const data = response.data;
          this.route_name = data.name || "";
          this.route_code = data.route_code || (data.id ? "R-" + String(data.id).padStart(4, "0") : "");
          this.full_trip_price = data.full_trip_price !== undefined && data.full_trip_price !== null ? data.full_trip_price : null;
          this.minimum_percentage = data.minimum_percentage !== undefined && data.minimum_percentage !== null ? data.minimum_percentage : null;
          this.weekly_discount_percentage = data.weekly_discount_percentage !== undefined && data.weekly_discount_percentage !== null ? data.weekly_discount_percentage : null;
          this.monthly_discount_percentage = data.monthly_discount_percentage !== undefined && data.monthly_discount_percentage !== null ? data.monthly_discount_percentage : null;
          this.canonicalDistance = data.distance ? parseFloat(data.distance).toFixed(2) : null;
          this.approximate_duration_server = data.approximate_duration || null;
          this.total_duration_seconds = data.total_duration_seconds || null;

          // Analytics data from enhanced API
          if (data.analytics) {
            this.analytics = { ...this.analytics, ...data.analytics };
          }

          // Directions
          this.directions = Array.isArray(data.directions) ? data.directions : [];

          // Stops
          let rawStops = Array.isArray(data.stops) ? data.stops : [];
          rawStops.forEach((stop, index) => {
            stop.order = index + 1;
            stop.lat = parseFloat(stop.lat);
            stop.lng = parseFloat(stop.lng);
          });
          this.stops = rawStops;

          // Polylines & Markers
          this.buildMapLayers();

          this.$nextTick(() => {
            this.fitMapBounds();
          });
        })
        .catch((error) => {
          console.error("Error fetching route details:", error);
          this.$notify({
            title: "خطأ",
            text: "فشل تحميل تفاصيل المسار وبيانات التشغيل",
            type: "error",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    changePeriod(p) {
      this.selectedPeriod = p;
      this.loadRouteDetails(p);
    },
    buildMapLayers() {
      // 1. Build Polyline from chosen directions only (current = 1)
      this.polyline = [];
      for (let i = 0; i < this.directions.length; i++) {
        const legDirs = this.directions[i];
        if (Array.isArray(legDirs)) {
          for (let j = 0; j < legDirs.length; j++) {
            const dir = legDirs[j];
            if (dir.current === 1 || dir.current === true) {
              this.polyline.push({
                data: dir.overview_path,
                strokeColor: this.colors[i % this.colors.length],
              });
            }
          }
        }
      }

      // 2. Build Markers with distinct icons for Start and End
      this.markers = [];
      for (let i = 0; i < this.stops.length; i++) {
        const stop = this.stops[i];
        const isStart = (i === 0);
        const isEnd = (i === this.stops.length - 1 && this.stops.length > 1);

        let iconUrl = "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";
        if (isStart) {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
        } else if (isEnd) {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/red-dot.png";
        }

        this.markers.push({
          place_id: stop.place_id || String(stop.id),
          position: { lat: stop.lat, lng: stop.lng },
          icon: iconUrl,
          infoText: `<strong>#${i + 1} - ${stop.name}</strong><br/>${stop.address || ''}`,
        });
      }

      if (this.stops.length > 0) {
        this.center = {
          lat: this.stops[0].lat,
          lng: this.stops[0].lng,
        };
      }
    },
    selectStop(stop, index) {
      this.selectedIdx = index;
      this.selectedItem = stop.place_id || String(stop.id);
      this.center = {
        lat: stop.lat,
        lng: stop.lng,
      };
      this.zoom = 15;
    },
    fitMapBounds() {
      if (this.$refs.mapLoader && typeof this.$refs.mapLoader.fitBounds === "function") {
        this.$refs.mapLoader.fitBounds();
      }
    },
    getLegDistance(index) {
      if (index === 0 || !this.directions || this.directions.length < index) return null;
      const legDirs = this.directions[index - 1];
      if (Array.isArray(legDirs)) {
        const currentDir = legDirs.find((d) => d.current == 1 || d.current === true) || legDirs[0];
        if (currentDir && currentDir.leg_distance !== undefined && currentDir.leg_distance !== null) {
          return currentDir.leg_distance;
        }
      }
      return null;
    },
    getLegDuration(index) {
      if (index === 0 || !this.directions || this.directions.length < index) return null;
      const legDirs = this.directions[index - 1];
      if (Array.isArray(legDirs)) {
        const currentDir = legDirs.find((d) => d.current == 1 || d.current === true) || legDirs[0];
        if (currentDir) {
          if (currentDir.leg_duration_text) {
            return currentDir.leg_duration_text;
          }
          if (currentDir.duration) {
            const sec = parseInt(currentDir.duration, 10);
            if (!isNaN(sec) && sec > 0) {
              return this.formatDurationArabic(sec);
            }
          }
        }
      }
      return null;
    },
    formatDurationArabic(totalSeconds) {
      if (!totalSeconds || totalSeconds <= 0) return null;
      const mins = Math.round(totalSeconds / 60);
      if (mins < 1) return "أقل من دقيقة";
      if (mins < 60) {
        if (mins === 1) return "دقيقة واحدة";
        if (mins === 2) return "دقيقتان";
        if (mins >= 3 && mins <= 10) return `${mins} دقائق`;
        return `${mins} دقيقة`;
      }
      const hours = Math.floor(mins / 60);
      const remMins = mins % 60;
      let hrText = "";
      if (hours === 1) hrText = "1 ساعة";
      else if (hours === 2) hrText = "2 ساعة";
      else if (hours >= 3 && hours <= 10) hrText = `${hours} ساعات`;
      else hrText = `${hours} ساعة`;

      if (remMins === 0) return hrText;

      let minText = "";
      if (remMins === 1) minText = "دقيقة واحدة";
      else if (remMins === 2) minText = "دقيقتان";
      else if (remMins >= 3 && remMins <= 10) minText = `${remMins} دقائق`;
      else minText = `${remMins} دقيقة`;

      return `${hrText} و ${minText}`;
    },
    getLegSummary(index) {
      if (index === 0 || !this.directions || this.directions.length < index) return null;
      const legDirs = this.directions[index - 1];
      if (Array.isArray(legDirs)) {
        const currentDir = legDirs.find((d) => d.current == 1 || d.current === true) || legDirs[0];
        if (currentDir && currentDir.summary) {
          return currentDir.summary;
        }
      }
      return null;
    },
    getStopProgressWidth(total) {
      return Math.round((total / this.maxStopActivity) * 100);
    },
    getPeakProgressWidth(count) {
      return Math.round((count / this.maxPeakActivity) * 100);
    },
    formatCurrency(val) {
      const num = parseFloat(val);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    navigateToEdit() {
      this.$router.push({
        name: "edit-route",
        params: {
          route_id: this.route_id,
          new_route_name: this.route_name,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800;900&display=swap');

.route-making-container {
  font-family: 'Cairo', sans-serif !important;
  color: #1E293B;
  background-color: #F8FAFC;
  min-height: calc(100vh - 80px);
  padding: 24px;

  * {
    font-family: 'Cairo', sans-serif !important;
    box-sizing: border-box;
  }
}

/* ==================== PAGE HEADER ==================== */
.rm-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-breadcrumb-wrap {
  font-size: 13px;
  color: #64748B;

  .rm-breadcrumb-link {
    color: #64748B;
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: #082C69;
    }
  }

  .rm-breadcrumb-current {
    color: #082C69;
    font-weight: 700;
  }
}

.rm-badge-section {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(8, 44, 105, 0.08);
  color: #082C69;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}

.rm-badge-analytics {
  background: rgba(16, 185, 129, 0.1);
  color: #10B981;
}

.rm-main-title {
  font-size: 24px;
  font-weight: 800;
  color: #082C69;
  line-height: 1.2;
}

.rm-subtitle {
  font-size: 13px;
  color: #64748B;
}

.rm-code-pill {
  display: inline-flex;
  align-items: center;
  background: #F1F5F9;
  border: 1px solid #CBD5E1;
  color: #082C69;
  font-weight: 800;
  font-size: 13px;
  padding: 3px 10px;
  border-radius: 8px;
  letter-spacing: 0.5px;
}

.rm-code-pill-lg {
  font-size: 15px;
  padding: 4px 12px;
  background: #E2E8F0;
  border-color: #94A3B8;
}

/* ==================== BUTTONS ==================== */
.rm-btn-primary {
  background: #082C69 !important;
  color: #FFFFFF !important;
  border-radius: 10px !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  letter-spacing: normal !important;
  padding: 0 18px !important;
  height: 42px !important;
  box-shadow: 0 2px 6px rgba(8, 44, 105, 0.2) !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #062152 !important;
    transform: translateY(-1px);
  }
}

.rm-btn-secondary {
  background: #FFFFFF !important;
  color: #334155 !important;
  border: 1px solid #CBD5E1 !important;
  border-radius: 10px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: normal !important;
  padding: 0 16px !important;
  height: 42px !important;
  transition: all 0.2s ease !important;

  &:hover {
    background: #F8FAFC !important;
    border-color: #94A3B8 !important;
  }
}

/* ==================== SUMMARY CARDS GRID ==================== */
.rm-cards-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.rm-stat-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(8, 44, 105, 0.08);
    border-color: #CBD5E1;
    transform: translateY(-2px);
  }
}

.rm-stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.rm-stat-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748B;
  margin-bottom: 4px;
}

.rm-stat-val-wrapper {
  margin-bottom: 2px;
}

.rm-stat-value {
  font-size: 18px;
  font-weight: 800;
  color: #0F172A;
  line-height: 1.2;
}

.rm-truncate-title {
  display: block;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rm-points-summary {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

.rm-point-tag {
  max-width: 70px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rm-start-tag {
  color: #10B981;
}

.rm-end-tag {
  color: #EF4444;
}

.rm-stat-caption {
  font-size: 11px;
  color: #94A3B8;
}

.rm-stat-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
}

.rm-icon-blue {
  background: rgba(8, 44, 105, 0.08);
}

.rm-icon-green {
  background: rgba(16, 185, 129, 0.1);
}

.rm-icon-amber {
  background: rgba(245, 158, 11, 0.12);
}

/* ==================== COMMON CARDS & LAYOUTS ==================== */
.rm-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-full-height {
  height: 100%;
}

.rm-card-title {
  font-size: 16px;
  font-weight: 800;
  color: #082C69;
  margin-bottom: 2px;
}

.rm-card-subtitle {
  font-size: 12px;
  color: #64748B;
  margin-bottom: 0;
}

.rm-icon-header-box {
  width: 40px;
  height: 40px;
  background: rgba(8, 44, 105, 0.06);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-paired-row {
  display: flex;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 1100px) {
    flex-direction: column;
  }
}

.rm-col-map {
  flex: 0 0 calc(45% - 10px);
  max-width: calc(45% - 10px);

  @media (max-width: 1100px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

.rm-col-stops {
  flex: 0 0 calc(55% - 10px);
  max-width: calc(55% - 10px);

  @media (max-width: 1100px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

.rm-col-pricing {
  flex: 0 0 50%;
  max-width: 50%;

  @media (max-width: 1024px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

.rm-col-subscriptions {
  flex: 0 0 50%;
  max-width: 50%;

  @media (max-width: 1024px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

.rm-col-chart {
  flex: 0 0 60%;
  max-width: 60%;

  @media (max-width: 1024px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

.rm-col-analytics-tabs {
  flex: 0 0 40%;
  max-width: 40%;

  @media (max-width: 1024px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

/* ==================== MAP STYLING ==================== */
.rm-map-view-wrapper {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #E2E8F0;
}

.rm-map-footer-legend {
  font-size: 11px;
}

.rm-legend-item {
  display: inline-flex;
  align-items: center;
  color: #475569;
  font-weight: 600;
}

.rm-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-left: 6px;
}

.rm-dot-start {
  background: #10B981;
}

.rm-dot-end {
  background: #EF4444;
}

.rm-dot-intermediate {
  background: #3B82F6;
}

/* ==================== STOPS TABLE ==================== */
.rm-stops-card {
  display: flex;
  flex-direction: column;
}

.rm-stops-scroll-wrapper {
  overflow-y: auto;
  overflow-x: auto;
  max-height: 520px;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  background: #FFFFFF;
}

.rm-modern-table.rm-stops-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: auto;

  thead th {
    background: #F8FAFC;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
    padding: 11px 12px;
    border-bottom: 2px solid #E2E8F0;
    white-space: nowrap;
    text-align: right;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  tbody tr {
    transition: background 0.15s ease;
    cursor: pointer;

    &:hover {
      background: #F8FAFC;
    }

    &.row-active {
      background: rgba(8, 44, 105, 0.04);
      border-right: 3px solid #082C69;
    }

    td {
      padding: 10px 12px;
      font-size: 12px;
      border-bottom: 1px solid #F1F5F9;
      color: #334155;
      vertical-align: middle;
    }
  }
}

.rm-th-order, .rm-td-order {
  width: 38px;
  min-width: 38px;
  max-width: 44px;
}

.rm-th-station, .rm-td-station {
  min-width: 120px;
}

.rm-th-address, .rm-td-address {
  min-width: 120px;
}

.rm-th-dist, .rm-td-dist {
  min-width: 75px;
  white-space: nowrap;
}

.rm-th-time, .rm-td-time {
  min-width: 75px;
  white-space: nowrap;
}

.rm-th-road, .rm-td-road {
  min-width: 105px;
}

.rm-order-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 11px;
}

.badge-first {
  background: #10B981;
  color: #FFFFFF;
}

.badge-last {
  background: #EF4444;
  color: #FFFFFF;
}

.badge-mid {
  background: #F1F5F9;
  color: #082C69;
}

.rm-station-info {
  display: flex;
  flex-direction: column;
}

.rm-stop-name-bold {
  font-weight: 700;
  color: #082C69;
  font-size: 13px;
  line-height: 1.3;
}

.rm-station-subtags {
  display: flex;
  align-items: center;
}

.rm-tag-first {
  font-size: 10px;
  background: rgba(16, 185, 129, 0.12);
  color: #10B981;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
}

.rm-tag-last {
  font-size: 10px;
  background: rgba(239, 68, 68, 0.12);
  color: #EF4444;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
}

.rm-address-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #64748B;
  font-size: 11px;
  line-height: 1.35;
  max-width: 180px;
}

.rm-pill-dist {
  background: #F1F5F9;
  color: #082C69;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  display: inline-block;
  white-space: nowrap;
}

.rm-pill-time {
  background: rgba(245, 158, 11, 0.12);
  color: #B45309;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  display: inline-block;
  white-space: nowrap;
}

.rm-pill-start {
  background: rgba(16, 185, 129, 0.1);
  color: #10B981;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 10px;
  display: inline-block;
  white-space: nowrap;
}

.rm-road-pill {
  display: inline-flex;
  align-items: center;
  max-width: 140px;
  background: rgba(8, 44, 105, 0.05);
  border: 1px solid rgba(8, 44, 105, 0.1);
  padding: 2px 7px;
  border-radius: 6px;
  color: #082C69;
  font-size: 11px;
  font-weight: 600;
}

.rm-road-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rm-cell-empty {
  color: #94A3B8;
  font-size: 12px;
}

.rm-table-count {
  font-size: 12px;
  font-weight: 700;
  color: #082C69;
  background: rgba(8, 44, 105, 0.08);
  padding: 3px 10px;
  border-radius: 6px;
}

/* ==================== PRICING CARD ==================== */
.rm-pricing-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.rm-price-box {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
}

.rm-box-highlight {
  background: rgba(8, 44, 105, 0.03);
  border-color: rgba(8, 44, 105, 0.15);
}

.rm-price-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748B;
  margin-bottom: 4px;
}

.rm-price-val {
  font-size: 20px;
  font-weight: 800;
  color: #0F172A;

  small {
    font-size: 12px;
    font-weight: 600;
    color: #64748B;
  }
}

.rm-price-caption {
  font-size: 10px;
  color: #94A3B8;
  margin-top: 2px;
}

.rm-price-empty {
  font-size: 14px;
  color: #94A3B8;
  font-weight: 600;
}

.rm-info-banner {
  background: rgba(8, 44, 105, 0.04);
  border: 1px solid rgba(8, 44, 105, 0.12);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  align-items: flex-start;

  .rm-info-text {
    font-size: 12px;
    color: #334155;
    line-height: 1.5;
  }
}

/* ==================== SUBSCRIPTIONS CARD ==================== */
.rm-sub-packages-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.rm-sub-pkg-card {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 14px 16px;
}

.rm-pkg-badge {
  font-size: 11px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
}

.rm-badge-weekly {
  background: rgba(59, 130, 246, 0.12);
  color: #2563EB;
}

.rm-badge-monthly {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.rm-pkg-discount {
  font-size: 11px;
  font-weight: 800;
  color: #F59E0B;
}

.rm-pkg-total-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

.rm-pkg-total-val {
  font-size: 16px;
  font-weight: 800;
}

.rm-field-hint {
  font-size: 11px;
  color: #64748B;
  line-height: 1.5;
}

/* ==================== ANALYTICS SECTION ==================== */
.rm-section-title {
  font-size: 18px;
  font-weight: 800;
  color: #082C69;
}

.rm-section-desc {
  font-size: 12px;
  color: #64748B;
}

.rm-period-selector {
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 3px;
  gap: 4px;
}

.rm-period-btn {
  background: transparent;
  border: none;
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #082C69;
  }

  &.period-active {
    background: #FFFFFF;
    color: #082C69;
    font-weight: 800;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

.rm-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px;
}

.rm-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;

  &:hover {
    border-color: #CBD5E1;
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  }
}

.rm-kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748B;
}

.rm-kpi-icon-box {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-kpi-val {
  font-size: 22px;
  font-weight: 800;
  color: #0F172A;
  margin-left: 4px;
}

.rm-kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
}

.rm-kpi-caption {
  font-size: 11px;
  color: #94A3B8;
  display: block;
  margin-top: 4px;
}

/* ==================== TABS & ACTIVITY LISTS ==================== */
.rm-tabs-pill-wrap {
  display: flex;
  background: #F1F5F9;
  border-radius: 10px;
  padding: 3px;
  width: 100%;
  gap: 4px;
}

.rm-tab-pill-btn {
  flex: 1;
  background: transparent;
  border: none;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &.active-tab {
    background: #FFFFFF;
    color: #082C69;
    font-weight: 800;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

.rm-station-activity-row,
.rm-peak-row {
  padding: 10px 12px;
  background: #F8FAFC;
  border: 1px solid #F1F5F9;
  border-radius: 10px;
  margin-bottom: 8px;
}

.rm-rank-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(8, 44, 105, 0.08);
  color: #082C69;
  font-size: 11px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-progress-track {
  width: 100%;
  height: 6px;
  background: #E2E8F0;
  border-radius: 3px;
  overflow: hidden;
}

.rm-progress-bar {
  height: 100%;
  background: #082C69;
  border-radius: 3px;
  transition: width 0.4s ease;
}

.rm-bar-amber {
  background: #F59E0B;
}

.rm-activity-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 240px;
}

.rm-chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
}

/* ==================== UTILITIES ==================== */
.border-top {
  border-top: 1px solid #F1F5F9;
}

.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

.text-primary-navy {
  color: #082C69 !important;
}

.text-amber-dark {
  color: #D97706 !important;
}
</style>
